import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'
import { MyClub } from './components/MyClub'
import { KYPSearch } from './components/KYPSearch'
import { RAGDocuments } from './components/RAGDocuments'
import { Navigation } from './components/Navigation'
import { FloatingFeedbackButton } from './components/FloatingFeedbackButton';
import { AuthProvider } from './lib/auth.context'
import { KYPSearchProvider } from './lib/kyp-search'
import { RAGDocumentsProvider } from './lib/rag-documents.context'
import { PlayerListProvider } from './lib/player-list.context'
import { FeedbackProvider } from './lib/feedback.context'
import { OADSearchProvider } from './lib/oad-search.context'
import { PlayerSimilarityProvider } from './lib/player-similarity.context'
import { ClubDataProvider } from './lib/club-data.context'
import { ProtectedRoute } from './components/shared/ProtectedRoute'
import { LoginPage } from './pages/auth/login'
import { ForgotPasswordPage } from './pages/auth/forgot-password'
import { ResetPasswordPage } from './pages/auth/reset-password'
import { CompleteInvitePage } from './pages/auth/complete-invite'

import { useAuth } from './lib/auth.context'
import { TOTPVerify } from './components/auth/totp-verify'
import { TOTPRegistration } from './components/auth/totp-registration'
import { TokenInitializer } from './components/auth/TokenInitializer'
import { Toaster } from './components/ui/toaster'
import SettingsPage from './pages/settings'
import { queryClient } from './lib/queryClient'

// Lazy-loaded components
const OADSearchPage = lazy(() => import('./pages/oad-search'));

function App() {
  // Force black theme on application load
  useEffect(() => {

    // Remove any theme preference from localStorage
    localStorage.removeItem('theme');

    // Apply black background to html and body elements
    document.documentElement.style.backgroundColor = '#000000';
    document.body.style.backgroundColor = '#000000';
    document.body.style.color = '#ffffff';
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <TokenInitializer>
            <ClubDataProvider>
              <KYPSearchProvider>
                <RAGDocumentsProvider>
                  <OADSearchProvider>
                    <PlayerListProvider>
                      <FeedbackProvider>
                        <PlayerSimilarityProvider>
                        <Routes>
                          {/* Public routes */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                        <Route
                          path="/auth/complete-invite"
                          element={
                            <ProtectedRoute requiresAuth={false} requiresInviteSession={true}>
                              <CompleteInvitePage />
                            </ProtectedRoute>
                          }
                        />

                        {/* Reset password routes with special protected access */}
                        <Route
                          path="/auth/reset-password"
                          element={
                            <ProtectedRoute requiresAuth={false} requiresRecoverySession={true}>
                              <ResetPasswordPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/reset-password"
                          element={
                            <ProtectedRoute requiresAuth={false} requiresRecoverySession={true}>
                              <ResetPasswordPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/auth/verify-totp" element={<TOTPVerify />} />
                        <Route path="/auth/totp-registration" element={<TOTPRegistration />} />

                        {/* Protected routes */}
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute>
                              <>
                                <Navigation />
                                <MyClub />
                              </>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/kyp-search"
                          element={
                            <ProtectedRoute>
                              <>
                                <Navigation />
                                <KYPSearch />
                              </>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/rag-documents"
                          element={
                            <ProtectedRoute>
                              <>
                                <Navigation />
                                <RAGDocuments />
                              </>
                            </ProtectedRoute>
                          }
                        />
                                          
                        <Route
                          path="/oad-search"
                          element={
                            <ProtectedRoute>
                              <>
                                <Navigation />
                                <Suspense fallback={<div>Loading...</div>}>
                                  <OADSearchPage />
                                </Suspense>
                              </>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <>
                                <Navigation />
                                <SettingsPage />
                              </>
                            </ProtectedRoute>
                          }
                        />
                        


                        {/* Catch all route */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>

                      {/* Floating Feedback Button */}
                      <FloatingFeedbackButton />

                      <Toaster />
                        </PlayerSimilarityProvider>
                      </FeedbackProvider>
                    </PlayerListProvider>
                  </OADSearchProvider>
                </RAGDocumentsProvider>
              </KYPSearchProvider>
            </ClubDataProvider>
          </TokenInitializer>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
